import React from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {
  SidebarLinkApprovals,
  SidebarLinkAssignedForms,
  SidebarLinkToolboxTalks,
} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {ListCurrentUserTasks} from 'src/app/tasks/Dashboard/ListCurrentUserTasks';
import {TasksOfflineBoundary} from 'src/app/tasks/TasksOfflineBoundary';
import {ListTaskDefinitions} from 'src/app/tasks/Dashboard/ListTaskDefinitions';
import {SupervisorApprovalList} from 'src/app/tasks/SupervisorApprovalList';
import {GetTaskTypeName, TaskPageTitleDict} from 'src/app/tasks/taskHelpers';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useTaskPermissions} from 'src/shared/auth/permissions/useTaskPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {TasksDashViewType} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const taskTypeIconMap: Partial<Record<TaskTypeEnum, React.ReactNode>> = {
  [TaskTypeEnum.Approval]: SidebarLinkApprovals.icon,
  [TaskTypeEnum.ToolboxTalk]: SidebarLinkToolboxTalks.icon,
  [TaskTypeEnum.Form]: SidebarLinkAssignedForms.icon,
};

const whitelistSupervisorApprovalTaskTypes = [TaskTypeEnum.ToolboxTalk];

export interface TasksDashPageProps {
  taskType: TaskTypeEnum;
  featureFlag: FeatureFlagEnum;
  module: ModuleEnum;
}

const TasksDashPage: React.FC<TasksDashPageProps> = ({
  taskType,
  featureFlag,
  module,
}) => {
  useSetDocumentTitle(GetTaskTypeName(taskType));

  const taskPerms = useTaskPermissions(module);

  const navigate = useAppNavigate();
  const {view} = useParams();
  const {isOffline} = useConnectivity();

  // Only the cached "my" list is served offline; a deep link to an admin tab
  // falls back to it rather than rendering a list that cannot load.
  const requestedView = (view ?? 'user') as TasksDashViewType;
  const selectedView = isOffline ? 'user' : requestedView;

  const isSupervisorApprovalEnabled =
    whitelistSupervisorApprovalTaskTypes.includes(taskType);

  const {RenderedTabsComponent} = useUrlQueryTabV2<TasksDashViewType>({
    view: selectedView,
    defaultView: 'user',
    tabs: [
      {
        view: 'user',
        label: `My ${TaskPageTitleDict[taskType].plural}`,
        component: <ListCurrentUserTasks taskType={taskType} />,
      },
      {
        view: 'supervisor-approval',
        disabled: !isSupervisorApprovalEnabled || isOffline,
        label: `Supervisor Approvals`,
        component: <SupervisorApprovalList taskType={taskType} />,
      },
      {
        view: 'manage',
        label: `Manage ${TaskPageTitleDict[taskType].plural}`,
        disabled: !taskPerms.view || isOffline,
        component: <ListTaskDefinitions taskType={taskType} module={module} />,
      },
    ],
    onSelectView: view => {
      navigate(PathUtility.TasksDashboard(taskType, view as TasksDashViewType));
    },
  });

  return (
    <TasksOfflineBoundary>
      <ContentWrapper featureFlag={featureFlag}>
        <PageHeader>
          <PageHeader.Title icon={taskTypeIconMap[taskType]}>
            {TaskPageTitleDict[taskType].plural}
          </PageHeader.Title>
        </PageHeader>
        {/* The banner rides with ListCurrentUserTasks on the "my" tab. */}
        {RenderedTabsComponent}
      </ContentWrapper>
    </TasksOfflineBoundary>
  );
};

// Register the routes with the new system
[
  {
    type: TaskTypeEnum.Approval,
    featureFlag: FeatureFlagEnum.Approvals,
    module: ModuleEnum.Approvals,
  },
  {
    type: TaskTypeEnum.ToolboxTalk,
    featureFlag: FeatureFlagEnum.ToolboxTalks,
    module: ModuleEnum.ToolboxTalks,
  },
  {
    type: TaskTypeEnum.Form,
    featureFlag: FeatureFlagEnum.Forms,
    module: ModuleEnum.AssignedForms,
  },
].forEach(item => {
  routeRegistry.registerPageRoute({
    backNavigation: 'page',
    path: PathUtility.TasksDashboard(item.type, ':view'),
    element: (
      <TasksDashPage
        taskType={item.type}
        featureFlag={item.featureFlag}
        module={item.module}
      />
    ),
    isPrivate: true,
    supportsOffline: true,
  });
});

export default TasksDashPage;
